<template>
  <GmapMap
    style="height: 100%"
    ref="map"
    :center="DEFAULT_CENTER"
    :zoom="zoom"
    @zoom_changed="onZoomChanged"
    :options="{
      zoomControl: true,
      mapTypeControl: true,
      mapTypeControlOptions: {
        style: google && google.maps.MapTypeControlStyle.DROPDOWN_MENU,
        position: google && google.maps.ControlPosition.TOP_RIGHT
      },
      scaleControl: false,
      streetViewControl: false,
      rotateControl: false,
      fullscreenControl: true,
      disableDefaultUi: false,
      gestureHandling: 'greedy'
    }"
  >
    <GmapCluster
      :zoomOnClick="true"
      :averageCenter="true"
      :minimumClusterSize="DEFAULT_MIN_CLUSTER_SIZE"
      :ignoreHidden="true"
      :imagePath="getPublicImgPath('img/maps/cluster/m')"
    >
      <GmapMarker
        :key="index"
        v-for="(m, index) in allMarkers"
        :position="m.position"
        :animation="m.animation"
        :clickable="true"
        :draggable="false"
        :icon="getPublicImgPath('img/maps/icon-user-' + m.status + (m.status === 1 && m.mock ? '-fake' : '') + '.png')"
        :title="`${m.user}  |  Grupo(s): ${m.groups}`"
        :visible="m.visible"
        @click="toggleInfoWindow(m, index, true)"
      />
    </GmapCluster>

    <GmapInfoWindow
      :options="infoOptions"
      :position="infoWindowPos"
      :opened="infoWinOpen"
      @closeclick="closeInfoWindow"
    >
      <div v-html="infoContent"></div>
    </GmapInfoWindow>
  </GmapMap>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from 'vue-property-decorator'
import { gmapApi } from 'vue2-google-maps'
import { UserMarker, MarkerPosition } from '@/types//Maps'
import { TrackingUser } from '@/types/Tracking'
import trackingService from '@/services/TrackingService'
import LogService from '../../services/support/LogService'
import config from '@/config'

/**
 * Component that shows the users positions
 */
@Component
export default class UsersLocationMap extends Vue {
  /**
   * Users ids to be shown
   */
  @Prop({ required: false, default: [] })
  status: number[]

  /**
   * Selected user
   */
  @Prop({ required: false })
  selected: TrackingUser

  /**
   * Perform zoom automaticaly
   */
  @Prop({ required: false, default: true })
  zoomAuto: boolean

  // Constants
  DEFAULT_CENTER = new MarkerPosition(25, -108)
  DEFAULT_ZOOM = 6
  DEFAULT_MIN_CLUSTER_SIZE = 25

  // Markers
  allMarkers: UserMarker[] = []
  allMarkersIds: number[] = []
  currentMarker?: UserMarker
  zoom = this.DEFAULT_ZOOM

  // Marker window
  infoContent = ''
  infoWindowPos = new MarkerPosition(0, 0)
  infoWinOpen = false
  currentMidx = -1
  infoOptions = {
    pixelOffset: {
      width: 0,
      height: -35
    }
  }

  // Map center control
  mapCentering = false
  usersBounds: any = ''

  mounted () {
    this.$root.$on('EVENT_TRACKING_APPLY_POSITIONS_CHANGES', this.applyUsersChanges)
    this.$root.$on('EVENT_TRACKING_ZOOM_USER', this.fitMapToCurrentUser)
    this.$root.$on('EVENT_TRACKING_ZOOM_ALLUSERS', this.fitMapToAllVisible)
    this.$root.$on('EVENT_TRACKING_CLEAN_MAP', this.cleanMap)
  }

  beforeDestroy () {
    this.$root.$off('EVENT_TRACKING_APPLY_POSITIONS_CHANGES', this.applyUsersChanges)
    this.$root.$off('EVENT_TRACKING_ZOOM_USER', this.fitMapToCurrentUser)
    this.$root.$off('EVENT_TRACKING_ZOOM_ALLUSERS', this.fitMapToAllVisible)
    this.$root.$off('EVENT_TRACKING_CLEAN_MAP', this.cleanMap)
  }

  get google () {
    return gmapApi()
  }

  get map (): any {
    return this.$refs.map
  }

  onZoomChanged (zoom: number) {
    this.zoom = zoom
  }

  /**
   * When user status are updated, filter the markers
   */
  @Watch('status')
  watchUserStatusFiltered () {
    this.infoWinOpen = false
    this.filterMarkers()
  }

  /**
   * When a user is selected, place the map to the user's position
   */
  @Watch('selected')
  watchUserSelected (user: TrackingUser) {
    if (this.currentMarker) {
      this.currentMarker.animation = 0

      if (!user.id && this.infoWinOpen) {
        this.toggleInfoWindow(this.currentMarker, this.currentMidx)
      }
    }

    if (user.id) {
      this.currentMarker = this.allMarkers.find(it => it.id === user.id)

      if (this.currentMarker) {
        this.currentMarker.animation = this.google.maps.Animation.BOUNCE
        this.fitMapToCurrentUser()
      }
    } else {
      this.fitMapToAllVisible()
    }
  }

  /**
   * Reset the map to initial state
   */
  cleanMap () {
    this.allMarkers = []
    this.allMarkersIds = []
    this.zoom = this.DEFAULT_ZOOM
    this.infoWinOpen = false
    this.currentMidx = -1
  }

  /**
   * Focus to the current user position
   */
  fitMapToCurrentUser () {
    if (this.currentMarker && this.currentMarker.position.hasValue()) {
      this.zoom = 17
      this.map.panTo(this.currentMarker.position)
    }
  }

  /**
   * Fit map in order to cover all visible markers
   */
  fitMapToAllVisible () {
    // If there is a selected user the map stay as is
    if (this.selected.id) return

    let bounds = new this.google.maps.LatLngBounds()
    let visibleMarkers = this.allMarkers.filter(it => it.visible)
    if (visibleMarkers.length) {
      visibleMarkers.forEach(it => bounds.extend(it.position))
      this.map.fitBounds(bounds)
    }
  }

  /**
   * When a user data is updated through events, the corresponding marker is updated
   */
  applyUsersChanges (users: TrackingUser[]) {
    try {
      let marker: UserMarker

      for (let user of users) {
        if (this.allMarkersIds.indexOf(user.id) > -1) {
          let marker = this.allMarkers.find(it => it.id === user.id)

          if (marker) {
            marker.position = MarkerPosition.fromString(user.position.position)
            marker.status = user.status
            marker.user = user.name
            marker.name = trackingService.getUserFullName(user)
            marker.mock = user.mockGps
            marker.visible = marker.position.hasValue() && this.isStatusVisible(user.status)

            this.refreshInfoWindow(marker)
          }
        } else if (user.position.position) {
          marker = this.createMarkerFromUser(user)
          this.allMarkers.push(marker)
          this.allMarkersIds.push(user.id)
        }
      }

      this.centerMap()
    } catch (e) {
      LogService.error(e)
    } finally {
      this.$emit('updated')
    }
  }

  /**
   * Create marker from an instance of TrackingUser
   */
  createMarkerFromUser (user: TrackingUser) {
    let marker = new UserMarker()
    marker.position = MarkerPosition.fromString(user.position.position)
    marker.id = user.id
    marker.user = user.name
    marker.name = trackingService.getUserFullName(user)
    marker.groups = trackingService.getUserGroupsNames(user)
    marker.status = user.status
    marker.mock = user.mockGps
    marker.visible = marker.position.hasValue() && this.isStatusVisible(user.status)
    return marker
  }

  /**
   * Filter markers to set markers visible in the map
   */
  filterMarkers () {
    this.allMarkers.forEach(it => {
      it.visible = it.position.hasValue() && this.status.indexOf(it.status) > -1
    })
  }

  /**
   * Center map process
   */
  centerMap () {
    // Center map. Wait 2 seconds
    if (this.zoomAuto && !this.mapCentering) {
      this.mapCentering = true
      setTimeout(() => {
        this.fitMapToAllVisible()
        this.mapCentering = false
      }, 2000)
    }
  }

  /**
   * Show / Hide info window from the selected user's marker
   */
  toggleInfoWindow (marker: UserMarker, idx: any, closeUser?: boolean) {
    this.infoWindowPos = marker.position
    this.infoContent = this.getInfoWindowContent(marker)

    if (this.currentMidx === idx) {
      // check if its the same marker that was selected if yes toggle
      this.infoWinOpen = !this.infoWinOpen
      if (closeUser && !this.infoWinOpen) {
        this.$emit('closeInfo')
        this.currentMidx = -1
      }
    } else {
      // if different marker set infowindow to open and reset current marker index
      this.infoWinOpen = true
      this.currentMidx = idx
      this.$emit('select', marker.user)
    }
  }

  /**
   * Close the active info window
   */
  closeInfoWindow () {
    this.infoWinOpen = false
    this.currentMidx = -1
    this.$emit('closeInfo')
  }

  /**
   * Refresh user marker to avoid presenting old info
   */
  refreshInfoWindow (marker: UserMarker) {
    if (this.infoWinOpen && this.currentMidx === this.allMarkers.indexOf(marker)) {
      this.toggleInfoWindow(marker, this.currentMidx)
      setTimeout(() => {
        if (marker) {
          this.toggleInfoWindow(marker, this.currentMidx)
        }
      }, 200)
    }
  }

  /**
   * Get the HTML content of the info window
   */
  getInfoWindowContent (marker: UserMarker) {
    return `
      <div class="bm-gm--iw">
        <h1>${marker.user}</h1>
        <h2>${marker.name}</h2>
      </div>
    `
  }

  /**
   * Check if a status is in the active status list
   */
  isStatusVisible (status: number) {
    return this.status.indexOf(status) > -1
  }

  getPublicImgPath (path: string) {
    return config.basePath + path
  }
}
</script>

<style lang="scss">
.google-map {
  height: 100%;
}

.gm-style .cluster {
  div {
    color: #fff !important;
    width: 53px !important;
    line-height: 55px !important;
  }
}

.gm-style .gm-style-iw-c {
  border-radius: 3px;
}

.gm-style .bm-gm--iw {
  text-align: center;
  h1 {
    font-size: 1rem;
    border-bottom: 1px solid #ccc;
    padding-bottom: 5px;
  }
  h2 {
    font-size: 0.75rem;
    color: #919191;
  }
}
</style>
